import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { resolvePathArray } from 'utils/functions/general';
import { useOnClickOutside } from 'utils/functions/hooks';
import {
  ExtendedFilterItemsWrapper,
  ExtendedFilterWrapper,
  ExtendedFitlerItem,
  ExtendedFilterLabel,
  FilterClearIcon,
  FilterDropDown,
  FilterDropDownCheckbox,
  FilterDropDownCheckboxLabel,
  FilterDropDownCheckboxWrapper,
  FilterDropDownItem,
  FilterDropDownWrapper,
  FilterLabel,
  FilterSearchBar,
  FilterSearchBarIcon,
  FilterSearchBarWrapper,
  FilterWrapper,
  FitlerSearchBarClearIcon,
  FilterDropDownActionWrapper,
  FilterDropDownClearButton,
  FilterDropDownApplyButton,
} from './Filter.style';

const Filter = ({
  extentedFilter,
  handleGenericFilterClick,
  filterProps,
  ordersApiCallParams,
  selectedMoreFiltersData,
  setSelectedMoreFiltersData,
}) => {
  const {
    filterLabel,
    filterDataValue,
    filterDataPath,
    filterObjMapper,
    filterSearchKey,
    filterSearchBarPlaceholder,
    filterType,
    filterDataValueCondition,
    filtersToClear,
  } = filterProps;

  const filterData = useSelector(state => {
    if (filterDataPath) return resolvePathArray(filterDataPath, state);
    return filterDataValue;
  });

  const [originalFilterData, setOriginalFilterData] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState('');
  const [selectedData, setSelectedData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredDropdownData, setFilteredDropdownData] = useState([]);

  const filterRef = useRef();
  useOnClickOutside(filterRef, () => setIsDropdownOpen(false));

  const handleToggle = () => {
    setIsDropdownOpen(prevState => !prevState);
  };

  const handleDataSelect = (key, value) => {
    let selectedDataTemp = { ...selectedData };

    if (filterSearchKey === undefined) {
      if (key === 0) {
        Object.keys(selectedDataTemp).forEach(selectedKey => {
          selectedDataTemp[selectedKey] = false;
        });
      } else selectedDataTemp[key] = selectedDataTemp[key] ? !selectedDataTemp[key] : value;
    } else if (key === 0) {
      selectedDataTemp = {};
      setSearchTerm('');
    } else if (selectedDataTemp[key] !== undefined) delete selectedDataTemp[key];
    else if (filterType === 'single') selectedDataTemp = { [key]: value };
    else selectedDataTemp = { ...selectedDataTemp, [key]: value };

    handleGenericFilterClick(filterSearchKey, selectedDataTemp, filtersToClear);

    Object.keys(selectedDataTemp).forEach(selectedKey => {
      if (selectedDataTemp[selectedKey] !== undefined && selectedDataTemp[selectedKey] === false)
        delete selectedDataTemp[selectedKey];
    });

    setSelectedData(selectedDataTemp);
  };

  const handleMultiDropdownFilterDataSelect = (key, value) => {
    let selectedDataTemp = { ...selectedData };

    if (selectedDataTemp[key] !== undefined) delete selectedDataTemp[key];
    else selectedDataTemp = { ...selectedDataTemp, [key]: value };

    setSelectedData(selectedDataTemp);
  };

  const handleMultiExtendedFilterDataSelect = (key, value) => {
    const selectedDataTemp = { ...selectedMoreFiltersData };
    if (selectedDataTemp.hasOwnProperty(filterSearchKey)) {
      if (selectedDataTemp[filterSearchKey][key] !== undefined)
        delete selectedDataTemp[filterSearchKey][key];
      else
        selectedDataTemp[filterSearchKey] = { ...selectedDataTemp[filterSearchKey], [key]: value };
    } else selectedDataTemp[filterSearchKey] = { [key]: value };
    setSelectedMoreFiltersData(selectedDataTemp);
  };

  const handleDataSearchOnChange = searchInput => {
    setSearchTerm(searchInput);
    const filteredData = originalFilterData.filter(item =>
      item[filterObjMapper.name].toLowerCase().includes(searchInput.toLowerCase()),
    );
    setFilteredDropdownData(filteredData);
  };

  const handleClearFilter = e => {
    e.stopPropagation();
    handleDataSelect(0);
    handleDataSearchOnChange('');
    setIsDropdownOpen(false);
  };

  const renderFilterLabel = () => {
    let filterLabelText = '';
    const selectedDataLength = Object.values(selectedData);
    if (selectedDataLength.length === 1) {
      filterLabelText = selectedDataLength['0'];
    } else if (selectedDataLength.length > 1)
      filterLabelText = `${filterLabel} (${selectedDataLength.length})`;
    else filterLabelText = filterLabel;
    return filterLabelText;
  };

  useEffect(() => {
    if (
      filterData?.length > 0 ||
      (filterDataValueCondition && Object.entries(filterData).length > 0)
    ) {
      let tempFilterData = filterData;
      if (filterDataValueCondition) {
        let filterValue = [];
        if (filterDataValueCondition)
          // eslint-disable-next-line no-restricted-syntax
          for (const [key, value] of Object.entries(filterDataValueCondition)) {
            if (ordersApiCallParams?.hasOwnProperty(key)) {
              if (Array.isArray(value)) {
                for (let i = 0; i < value.length; i += 1) {
                  const val = value[i].toString();
                  if (ordersApiCallParams[key].includes(val)) {
                    if (filterData.hasOwnProperty(val))
                      filterValue = [...filterValue, ...filterData[val]];
                  }
                }
              }
            }
          }
        tempFilterData = [...filterValue];
        setFilteredDropdownData([...tempFilterData]);
      } else setFilteredDropdownData(tempFilterData);
      setOriginalFilterData(tempFilterData);
    }
  }, [filterData, ordersApiCallParams, filterDataValueCondition]);

  useEffect(() => {
    if (Object.keys(ordersApiCallParams).length > 0) {
      const tempSelectedValue = ordersApiCallParams[filterSearchKey];
      if (tempSelectedValue) {
        const filterDataLength = filterData?.length;
        if (filterDataLength) {
          const filterObjMapperKey = filterObjMapper.key;
          const tempSelectedName = filterData.find(
            val => val[filterObjMapperKey] === tempSelectedValue,
          )?.name;
          setSelectedData({ [tempSelectedValue]: tempSelectedName });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData]);

  const renderDropdownFilter = () => (
    <FilterWrapper ref={filterRef}>
      <FilterLabel
        onClick={() => handleToggle()}
        toggled={isDropdownOpen}
        selected={Object.values(selectedData).length > 0}
      >
        {renderFilterLabel()}
        {Object.values(selectedData).length > 0 && (
          <FilterClearIcon onClick={e => handleClearFilter(e)} />
        )}
      </FilterLabel>
      {isDropdownOpen && (
        <FilterDropDownWrapper>
          <FilterSearchBarWrapper>
            <FilterSearchBarIcon />
            <FilterSearchBar
              placeholder={filterSearchBarPlaceholder}
              value={searchTerm}
              onChange={e => handleDataSearchOnChange(e.target.value)}
            />
            {searchTerm && (
              <FitlerSearchBarClearIcon onClick={() => handleDataSearchOnChange('')} />
            )}
          </FilterSearchBarWrapper>
          <FilterDropDown>
            <FilterDropDownItem key={0} onClick={() => handleDataSelect(0)}>
              {`All ${filterLabel}`}
            </FilterDropDownItem>
            {filteredDropdownData?.map(item => (
              <FilterDropDownCheckboxWrapper
                onClick={() =>
                  filterType === 'single'
                    ? handleDataSelect(item[filterObjMapper.key], item[filterObjMapper.name])
                    : handleMultiDropdownFilterDataSelect(
                        item[filterObjMapper.key],
                        item[filterObjMapper.name],
                      )
                }
              >
                {filterType !== 'single' && (
                  <FilterDropDownCheckbox
                    key={item[filterObjMapper.key]}
                    type="checkbox"
                    readOnly
                    value={item[filterObjMapper.key]}
                    checked={selectedData[item[filterObjMapper.key]] !== undefined}
                  />
                )}
                <FilterDropDownCheckboxLabel>
                  {item[filterObjMapper.name]}
                </FilterDropDownCheckboxLabel>
              </FilterDropDownCheckboxWrapper>
            ))}
          </FilterDropDown>
          {filterType !== 'single' && (
            <FilterDropDownActionWrapper>
              <FilterDropDownClearButton onClick={e => handleClearFilter(e)}>
                Clear
              </FilterDropDownClearButton>
              <FilterDropDownApplyButton
                onClick={() => {
                  handleGenericFilterClick(filterSearchKey, selectedData);
                  handleToggle();
                }}
              >
                Apply
              </FilterDropDownApplyButton>
            </FilterDropDownActionWrapper>
          )}
        </FilterDropDownWrapper>
      )}
    </FilterWrapper>
  );

  const renderExtendedFilter = () => (
    <ExtendedFilterWrapper>
      <ExtendedFilterLabel>{filterLabel}</ExtendedFilterLabel>
      <FilterSearchBarWrapper>
        <FilterSearchBarIcon />
        <FilterSearchBar
          placeholder={filterSearchBarPlaceholder}
          value={searchTerm}
          onChange={e => handleDataSearchOnChange(e.target.value)}
        />
        {searchTerm && <FitlerSearchBarClearIcon onClick={() => handleDataSearchOnChange('')} />}
      </FilterSearchBarWrapper>
      <ExtendedFilterItemsWrapper>
        {filteredDropdownData?.map(item => {
          const isChecked =
            selectedMoreFiltersData[filterSearchKey] &&
            selectedMoreFiltersData[filterSearchKey][item[filterObjMapper.key]];
          return (
            <ExtendedFitlerItem
              key={item[filterObjMapper.key]}
              onClick={() =>
                handleMultiExtendedFilterDataSelect(
                  item[filterObjMapper.key],
                  item[filterObjMapper.name],
                )
              }
            >
              {filterType !== 'single' && (
                <FilterDropDownCheckbox
                  type="checkbox"
                  readOnly
                  value={item[filterObjMapper.key]}
                  checked={isChecked}
                />
              )}
              <FilterDropDownCheckboxLabel>
                {item[filterObjMapper.name]}
              </FilterDropDownCheckboxLabel>
            </ExtendedFitlerItem>
          );
        })}
      </ExtendedFilterItemsWrapper>
    </ExtendedFilterWrapper>
  );

  return <div>{!extentedFilter ? renderDropdownFilter() : renderExtendedFilter()}</div>;
};

Filter.propTypes = {
  extentedFilter: PropTypes.bool,
  handleGenericFilterClick: PropTypes.func.isRequired,
  filterProps: PropTypes.shape({
    filterLabel: PropTypes.string,
    filterDataValue: PropTypes.arrayOf(PropTypes.shape({})),
    filterDataPath: PropTypes.arrayOf(PropTypes.string),
    filterObjMapper: PropTypes.shape({
      key: PropTypes.string,
      name: PropTypes.string,
    }),
    filterSearchKey: PropTypes.string,
    filterSearchBarPlaceholder: PropTypes.string,
    filterType: PropTypes.string,
    filterDataValueCondition: PropTypes.shape({}),
    filtersToClear: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  ordersApiCallParams: PropTypes.shape({}),
  selectedMoreFiltersData: PropTypes.shape({}),
  setSelectedMoreFiltersData: PropTypes.func,
};

Filter.defaultProps = {
  extentedFilter: false,
  selectedMoreFiltersData: {},
  ordersApiCallParams: {},
  setSelectedMoreFiltersData: () => {},
};

export default Filter;
